import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store/store';
import { fetchEquipment } from '../../features/equipment/equipmentSlice';
import EquipmentCard from './EquipmentCard';
import AddEquipmentForm from './AddEquipmentForm';
import Button from '../ui/Button';
import SearchInput from '../ui/SearchInput';
import LoadingSpinner from '../ui/LoadingSpinner';
import ErrorMessage from '../ui/ErrorMessage';
import { Equipment } from '../../types/equipment';

const EquipmentList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { equipment, loading, error } = useSelector((state: RootState) => state.equipment);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<Equipment['status'] | 'all'>('all');

  useEffect(() => {
    dispatch(fetchEquipment());
  }, [dispatch]);

  const filteredEquipment = equipment.filter(item => {
    const matchesSearch = 
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:justify-between sm:items-center">
        <h2 className="text-2xl font-bold text-gray-900">{t('equipment.title')}</h2>
        <Button 
          size="sm"
          onClick={() => setShowAddForm(!showAddForm)}
          className="w-full sm:w-auto"
        >
          {showAddForm ? t('common.cancel') : t('equipment.addEquipment')}
        </Button>
      </div>

      {showAddForm && (
        <AddEquipmentForm onSuccess={() => setShowAddForm(false)} />
      )}

      {/* Filters Section */}
      <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:gap-4">
        <div className="flex-1">
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('common.search')}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as Equipment['status'] | 'all')}
          className="w-full sm:w-48 rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
          aria-label={t('common.status')}
        >
          <option value="all">{t('common.allStatus')}</option>
          <option value="active">{t('equipment.status.active')}</option>
          <option value="maintenance">{t('equipment.status.maintenance')}</option>
          <option value="retired">{t('equipment.status.retired')}</option>
        </select>
      </div>

      {/* Equipment Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredEquipment.length === 0 ? (
          <p className="text-gray-500 col-span-full text-center py-8">
            {t('equipment.noEquipment')}
          </p>
        ) : (
          filteredEquipment.map((item) => (
            <EquipmentCard key={item.id} equipment={item} />
          ))
        )}
      </div>
    </div>
  );
};

export default EquipmentList;